import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { Group, Page } from '../../../../types';
import DeleteGroupsDialog from './delete-groups-dialog';
import BulkApplyTemplateDialog from './group-management/bulk-apply-template-dialog';
import { useNavigate } from 'react-router-dom';
import GroupsGridToolbar from './groups-toolbar';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import CloneGroupsDialog from './clone-group-dialog';
import { Messages } from '../../../utils/message-constants';
import AddGroupDialog from './add-group-dialog';
import { useUserSettings } from '../../../utils/use-user-settings';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getDomainsQuery, getGroups, UserKeys } from '../../../api/users-api';
import { pagesConfig } from '../../../shared/pages-config';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';

import Tooltip from '@mui/material/Tooltip';
import GroupRemove from '@mui/icons-material/GroupRemove';
import LibraryAdd from '@mui/icons-material/LibraryAdd';

export default function GroupsGrid() {
  const {
    limitedVisDomainList,
    directoryRoles,
    changeLogRoles,
    limitedVisEnabled,
    viewType,
  } = useUserSettings();
  const [openAddGroup, setOpenAddGroup] = useState(false);
  const [openDeleteGroups, setOpenDeleteGroups] = useState(false);
  const [openCloneGroup, setOpenCloneGroup] = useState(false);
  const [openApplyTemplate, setOpenApplyTemplate] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const navigate = useNavigate();

  const targetGroupIds = useMemo(
    () => Object.fromEntries(selectedRows.map((email) => [email, ''])),
    [selectedRows]
  );

  const { data: allDomains } = useQuery(getDomainsQuery());

  const domains = limitedVisEnabled ? limitedVisDomainList : allDomains || [];

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'name',
        headerName: 'Name',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'directMemberCount',
        headerName: 'Member Count',
        editable: false,
        sortable: false,
        filterable: false,
        width: 130,
        align: 'center',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params: GridRowParams<Group>) => [
          <Tooltip
            key="clone"
            arrow
            title={
              !isAbleToSeeUIElement(
                directoryRoles,
                DirectoryRoles.CloneGroup.value
              )
                ? Messages.Errors.Permission.Acting(
                    DirectoryRoles.CloneGroup.name
                  )
                : 'Clone'
            }
          >
            <span>
              <GridActionsCellItem
                label="Clone"
                icon={<LibraryAdd />}
                disabled={
                  !isAbleToSeeUIElement(
                    directoryRoles,
                    DirectoryRoles.CloneGroup.value
                  )
                }
                onClick={() => {
                  setSelectedGroup(params.row);
                  setOpenCloneGroup(true);
                }}
              />
            </span>
          </Tooltip>,
        ],
      },
    ],
    [directoryRoles]
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'groupsGridStates'
  );

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 50,
    page: initialState?.pagination?.paginationModel?.page || 0,
    cursor: null,
    search: initialState?.filter?.filterModel?.quickFilterValues?.[0] || '',
  });

  const {
    data: groups,
    isLoading: groupsLoading,
    isFetching: groupsFetching,
  } = useQuery({
    queryKey: [
      UserKeys.Groups,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.search,
    ],
    queryFn: () => {
      const object: Page<Group[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      if (paginationModel.page !== 0 && groups && groups.queries) {
        object.queries = {
          [Object.keys(groups.queries)[0]]: paginationModel.cursor || '',
        };
      }
      return getGroups(object, paginationModel.search);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('groups'),
    },
  });

  const toolbarProps = useMemo(
    () => ({
      paginationModel,
      setPaginationModel,
      setOpenAddGroup,
      setOpenDeleteGroups,
      setOpenApplyTemplate,
      selectedRows,
    }),
    [paginationModel, selectedRows]
  );

  return (
    <>
      <SharedDataGridServer
        gridKey={viewType.groupsGridStates?.activeGridState || 'default'}
        slots={{
          toolbar: useToolbar(GroupsGridToolbar, toolbarProps),
          ...createGridOverlays(
            'Groups',
            <GroupRemove />,
            paginationModel.search !== ''
              ? 'Try changing your search term.'
              : ''
          ),
        }}
        initialState={initialState || {}}
        checkboxSelection
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        getRowId={(row) => row.email}
        data={groups}
        columns={computedColumns}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (groupsLoading || groupsFetching) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              search: paginationModel.search,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: groups?.cursor || '',
              search: paginationModel.search,
            });
          }
        }}
        onFilterModelChange={(model) => {
          if (groupsLoading || groupsFetching) return;

          setPaginationModel({
            ...paginationModel,
            page: 0,
            cursor: null,
            search: model.quickFilterValues?.[0] || '',
          });
        }}
        loading={groupsLoading || groupsFetching}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        onRowSelectionModelChange={(newModel) => {
          if (newModel.ids.size === 0) {
            setSelectedGroup(undefined);
            return;
          }
          const selectedGroupVal = groups?.results?.find(
            (group) =>
              group.email !== undefined && newModel.ids.has(group.email)
          );
          if (selectedGroupVal) setSelectedGroup(selectedGroupVal);
        }}
        setSelectedRows={setSelectedRows}
        onRowClick={(row: GridRowParams<Group>) =>
          setTimeout(() => {
            navigate(`/${pagesConfig.groups.link}/${row.row.email}`);
          }, 50)
        }
        selectedRows={selectedRows}
      />
      {openAddGroup && (
        <AddGroupDialog
          open={openAddGroup}
          handleClose={() => {
            setOpenAddGroup(false);
          }}
          domains={domains}
        />
      )}
      {openDeleteGroups && selectedGroup && (
        <DeleteGroupsDialog
          open={openDeleteGroups}
          handleClose={() => {
            setOpenDeleteGroups(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          selectedGroup={selectedGroup}
        />
      )}
      {openApplyTemplate && selectedRows.length > 0 && (
        <BulkApplyTemplateDialog
          open={openApplyTemplate}
          handleClose={() => setOpenApplyTemplate(false)}
          targetGroupIds={targetGroupIds}
          setSelectedRows={setSelectedRows}
          directoryRoles={directoryRoles}
          changeLogRoles={changeLogRoles}
        />
      )}
      {openCloneGroup && selectedGroup && (
        <CloneGroupsDialog
          open={openCloneGroup}
          handleClose={() => {
            setOpenCloneGroup(false);
          }}
          selectedGroup={selectedGroup}
          domains={domains}
        />
      )}
    </>
  );
}
